// Personnel File tab — admin-only home for instructor HR records.
// Sections: Tax, 1099, Documents, Compensation, Emergency, Notes, Compliance, Audit.
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Lock } from "lucide-react";
import TaxInformationSection from "./TaxInformationSection";
import TenNinetyNineSection from "./TenNinetyNineSection";
import DocumentsSection from "./DocumentsSection";
import CompensationSection from "./CompensationSection";
import EmergencyContactSection from "./EmergencyContactSection";
import InternalNotesSection from "./InternalNotesSection";
import ComplianceChecklist from "./ComplianceChecklist";
import AuditLogSection from "./AuditLogSection";

export default function PersonnelFileTab({ instructor }: { instructor: any }) {
  const { isAdmin, loading } = useAuth();
  const [w9Request, setW9Request] = useState<any>(null);
  const [documents, setDocuments] = useState<any[]>([]);
  const [reloadKey, setReloadKey] = useState(0);
  const bump = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    (async () => {
      const [w9, docs] = await Promise.all([
        (supabase.from as any)("instructor_w9_requests")
          .select("*, packet:instructor_onboarding_packets!inner(instructor_id)")
          .eq("packet.instructor_id", instructor.id)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
        (supabase.from as any)("instructor_documents")
          .select("*")
          .eq("instructor_id", instructor.id)
          .is("deleted_at", null)
          .order("uploaded_at", { ascending: false }),
      ]);
      if (cancelled) return;
      setW9Request(w9.data ?? null);
      setDocuments(docs.data ?? []);
    })();
    return () => { cancelled = true; };
  }, [instructor.id, isAdmin, reloadKey]);

  if (loading) return null;
  if (!isAdmin) {
    return (
      <Alert>
        <Lock className="h-4 w-4" />
        <AlertDescription>
          The Personnel File is restricted to administrators.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Lock className="h-4 w-4 text-muted-foreground" /> Personnel File
          </CardTitle>
          <CardDescription>
            Admin-only. Every view and download of tax documents is audited.
          </CardDescription>
        </CardHeader>
      </Card>

      <ComplianceChecklist instructor={instructor} documents={documents} />
      <TaxInformationSection instructor={instructor} w9Request={w9Request} onChanged={bump} />
      <TenNinetyNineSection instructor={instructor} />
      <DocumentsSection instructor={instructor} documents={documents} onChanged={bump} />
      <CompensationSection instructor={instructor} />
      <EmergencyContactSection instructor={instructor} />
      <InternalNotesSection instructor={instructor} />
      <AuditLogSection instructor={instructor} />
    </div>
  );
}
